import type { OnboardingState } from '@/hooks/api/useOnboarding';

/**
 * Is the console off-limits until this workspace finishes onboarding?
 *
 * Pure, and separated from the hook so the truth table can be pinned without a
 * DOM — see `consoleLock.test.ts`, which is the "never lock an existing working
 * workspace" guarantee.
 *
 * THE SERVER OWNS THE RULE. `locked` is computed by `flow.is_locked` in the
 * backend, because it decides whether a paying customer can reach their own
 * product and a rule that important gets one implementation. This reads that
 * field and only falls back to deriving it so an older backend cannot lock
 * anyone.
 *
 * EVERY UNKNOWN ANSWER IS OPEN. A loading state, a network blip, a 404, an
 * errored query or a workspace we have no answer for must never black out a
 * working console — the failure mode that matters here is locking someone out,
 * not letting them briefly see a page they cannot use.
 */
export function isWorkspaceLocked(state: OnboardingState | undefined): boolean {
  if (!state) return false; // no answer yet, disabled, or errored
  if (typeof state.locked === 'boolean') return state.locked;

  // Fallback, in the same order as the server's:
  if (state.step === 'done') return false;
  // `step === null` is a COMPANY THAT EXISTS and simply never entered the
  // sequenced flow — every workspace that predates the wizard, and every
  // prospect workspace the unattended chain researches. This line is the one
  // that keeps them out of the lock, and it is the single most important line in
  // the file.
  if (state.step === null) return false;
  // `=== true`, never truthy: if the field is ever absent it must read OPEN.
  return state.gated === true;
}
